import { TYPES_MAP } from '@a0/support-center-common/constants/tickets/types';
import { isTrialTenant } from '@a0/support-center-common/lib/tenants';
import {getAdditionalQuestions} from '../../utils/fields';
/**
 * Infer if an appliance subscription is provided
 * @param  {Object} subscription
 * @return {Boolean}
 */
function isAppliance(subscription) {
  return subscription && !!subscription.is_appliance;
}

/**
 * If a tenant is in trial
 * @param  {Object} tenant
 * @return {Boolean}
 */
function isTrial(tenant) {
  return isTrialTenant(tenant);
}

/**
 * True if a tenant is currently selectected in the ticket form
 * @param  {Object} ticket
 * @return {Boolean}
 */
function isTenantSelected(ticket) {
  return !!ticket.applianceTenant || !!ticket.tenant;
}


/**
 * True if a severity is expected according to current ticket data
 * @param  {Object} ticket
 * @param  {Object} tenant
 * @return {Boolean}
 */
function severityRequired(ticket, tenant) {
  if (isTrial(tenant)) {
    return false;
  }
  const ticketType = TYPES_MAP[ticket.type];
  return ticketType && ticketType.severities && ticketType.severities.length > 1;
}

function hasAdditionalQuestions(ticket) {
  const questions= getAdditionalQuestions(ticket);
  return questions.length > 0;
}

/**
 * True if a the user selected a 'type' for the current ticket
 * @param  {Object} ticket
 * @return {Boolean}
 */
function isTypeSelected(ticket) {
  return !!ticket.type;
}

/**
 * True if a the user selected a 'severity' for the current ticket
 * @param  {Object} ticket
 * @return {Boolean}
 */
function isSeveritySelected(ticket) {
  return !!ticket.customerSelectedSeverity;
}

/**
 * True if severity is neither required or selected
 * @param  {Object} ticket
 * @return {Boolean}
 */
function severityNotRequiredOrSelected(ticket, tenant) {
  return (
    (!severityRequired(ticket, tenant) || !!ticket.customerSelectedSeverity)
  );
}

/**
 * True if type and severity have been selected
 * @param  {Object} ticket
 * @return {Boolean}
 */
function isTypeAndSeveritySelected(ticket, tenant) {
  return (
    isTypeSelected(ticket) &&
    severityNotRequiredOrSelected(ticket, tenant)
  );
}

/**
 * Rules for determining if a field should be displayed in the form
 * Each function corresponds to one form field
 * Each receives the ticket form values, selected subscription and selected tenant as arguments
 * Truthy return values display the field, false hides it
 */
export default {
  tenant() {
    return true;
  },
  rootTenantAuthority(ticket, selectedSubscription) {
    return isAppliance(selectedSubscription);
  },
  applianceTenant(ticket, selectedSubscription) {
    return isAppliance(selectedSubscription) && ticket.rootTenantAuthority;
  },
  type(ticket, selectedSubscription) {
    return isTenantSelected(ticket);
  },
  customerSelectedSeverity(ticket, selectedSubscription, selectedTenant) {
    return (
      isTenantSelected(ticket) &&
      !isTrial(selectedTenant) &&
      isTypeSelected(ticket) &&
      severityRequired(ticket, selectedTenant)
    );
  },
  additionalQuestions(ticket, selectedSubscription) {
    return (
      isTenantSelected(ticket) &&
      isTypeSelected(ticket) &&
      hasAdditionalQuestions(ticket)
    );
  },
  subject(ticket, selectedSubscription, selectedTenant) {
    return (
      isTenantSelected(ticket) &&
      isTypeAndSeveritySelected(ticket, selectedTenant)
    );
  },
  comment(ticket, selectedSubscription, selectedTenant) {
    return (
      isTenantSelected(ticket) &&
      isTypeAndSeveritySelected(ticket, selectedTenant)
    );
  },
  collaborators(ticket, selectedSubscription, selectedTenant) {
    return (
      isTenantSelected(ticket) &&
      isTypeAndSeveritySelected(ticket, selectedTenant)
    );
  },
  submitButton(ticket, selectedSubscription, selectedTenant) {
    return (
      isTenantSelected(ticket) &&
      isTypeAndSeveritySelected(ticket, selectedTenant)
    );
  }
};
